import React, { useState, useEffect } from "react";
import { useSample } from "./utils/useSample";
import { useNavigate } from "react-router-dom";
import {
  GridRowParams,
  GridSortDirection,
  GridSortModel,
} from "@mui/x-data-grid";
import pathRoutes from "../../routes/pathRoutes";
import { columnsSample } from "./utils/sample.columns";
import FilterSample from "./components/filterSample";
import { Box } from "@mui/material";
import { CreatePageList } from "@app/components/Layouts/PageList";
import { Add } from "@app/components/Actions";
import { CustomDataGrid, Loading, MButton } from "@app/components";
import useTabs from "./utils/useTab";
import useAnalysis from "./utils/useAnalysis";
import {
  SampleRegistrationTypes,
  IFormSamplesInputs,
} from "@app/types/samples.types";
import AuditModal from "./components/modals/AuditModal";
import KeeperModal from "./components/modals/KeeperModal";
import BulkDuplicateModal from "./components/modals/BulkDuplicateModal";
import { PermissionComponentGate } from "@app/utils/PermissionComponentGate";
import { RolesTypes } from "../users/utils/helpers";

const SampleList = () => {
  const {
    filteredSample,
    getAndSet,
    stateSample,
    exportPlan,
    loadingExport,
    samplesList,
    totalItems,
    page,
    limit,
    orderBy,
    order,
    setPage,
    setLimit,
    setOrderBy,
    setOrder,
    fetchAll,
    fetchAllIds,
  } = useSample();
  const navigate = useNavigate();
  const dataSamples = filteredSample();
  const { setPhaseSelected, setTabValue } = useTabs();
  const [openModalAudit, setOpenModalAudit] = useState(false);
  const [openModalKeeper, setOpenModalKeeper] = useState<boolean>(false);
  const [openModalBulkDuplicate, setOpenModalBulkDuplicate] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const { preparationsList, loading: loadingAnalysis } = useAnalysis();
  const [sampleFlow, setSampleFlow] = useState<
    SampleRegistrationTypes | undefined
  >();
  const [filters, setFilters] = React.useState<IFormSamplesInputs>({});
  const [selectedSamples, setSelectedSamples] = React.useState<number[]>([]);

  useEffect(() => {
    fetchAll();
  }, [filters, page, limit, orderBy, order]);

  const handleRowDoubleClick = (params: GridRowParams): void => {
    setPhaseSelected("initial");
    setTabValue(0);
    navigate(`${pathRoutes.PORTAL_SAMPLES}/analisis/${params.id}`);
  };

  const handleOpenAudit = async (id: number) => {
    setLoading(true);
    setOpenModalAudit(true);
    const sampleService = await getAndSet(id);
    setSampleFlow(sampleService);
    setLoading(false);
  };

  const handleExportPlan = async () => {
    await exportPlan(filters);
  };

  const handleClose = () => {
    setOpenModalAudit(false);
  };

  const handleOpenModalKeeper = () => {
    setOpenModalKeeper(true);
  };

  const handleOpenModalBulkDuplicate = () => {
    if (selectedSamples.length === 0) {
      alert("Por favor, seleccione al menos una muestra para duplicar");
      return;
    }
    setOpenModalBulkDuplicate(true);
  };

  const handleBulkDuplicateSuccess = () => {
    setSelectedSamples([]);
    fetchAll();
  };

  const handleRowSelectionChange = (newSelection: any) => {
    setSelectedSamples(newSelection as number[]);
  };

  const handleSelectAll = async () => {
    setLoading(true);
    try {
      const allIds = await fetchAllIds();
      setSelectedSamples(allIds);
    } catch (error) {
      console.error("Error selecting all samples:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeselectAll = () => {
    setSelectedSamples([]);
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setOrderBy(sortModel.length > 0 ? sortModel[0].field : '');
    setOrder(sortModel.length > 0 ? sortModel[0].sort : null);
  };

  return (
    <CreatePageList
      title="Listado de muestras"
      buttonRight={
        <PermissionComponentGate
          permissions={[RolesTypes.ADMIN, RolesTypes.SUPER_ADMIN]}
        >
          <Add to={`portal/muestras/crear`} title="Nueva muestra" />
        </PermissionComponentGate>
      }
    >
      <FilterSample
        onFilterSubmit={(f) => {
          setFilters(f);
          setPage(1);
        }}
      />
      <Box display="flex" justifyContent="space-between" mt={2} gap={2}>
        <Box display="flex" gap={1} alignItems="center">
          <MButton
            variant="outlined"
            color="primary"
            text={`Seleccionar todas`}
            onClick={handleSelectAll}
            disabled={loading}
          />
          {selectedSamples.length > 0 && (
            <>
              <MButton
                variant="outlined"
                color="secondary"
                text="Deseleccionar todas"
                onClick={handleDeselectAll}
              />
              <MButton
                variant="contained"
                color="primary"
                text={`Duplicar masivamente (${selectedSamples.length})`}
                onClick={handleOpenModalBulkDuplicate}
              />
            </>
          )}
        </Box>
        <Box display="flex" gap={2}>
          <PermissionComponentGate
            permissions={[RolesTypes.ADMIN, RolesTypes.SUPER_ADMIN]}
          >
            <MButton
              variant="outlined"
              color="secondary"
              text="Plan de Muestreo"
              onClick={handleExportPlan}
            />
            <MButton
              variant="outlined"
              color="secondary"
              text="Keeper"
              onClick={handleOpenModalKeeper}
            />
          </PermissionComponentGate>
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", mt: 1 }}>
        <CustomDataGrid
          rows={samplesList}
          columns={columnsSample(handleOpenAudit)}
          handleRowDoubleClick={handleRowDoubleClick}
          paginationModel={{ page: page - 1, pageSize: limit }}
          onPaginationModelChange={(model) => {
            setPage(model.page + 1);
            setLimit(model.pageSize);
          }}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          rowCount={totalItems}
          loading={loading}
          search={false}
          columnVisible={{
            id: false,
            sampling: false,
            sampleManagement: false,
            conditioning: false,
            processing: false,
          }}
          checkboxSelection={true}
          onRowSelectionModelChange={handleRowSelectionChange}
          rowSelectionModel={selectedSamples}
        />
      </Box>

      <AuditModal
        open={openModalAudit}
        onClose={handleClose}
        loading={loading}
        loadingAnalysis={loadingAnalysis}
        sampleFlow={sampleFlow}
        stateSample={stateSample}
        preparationsList={preparationsList}
      />
      <KeeperModal
        open={openModalKeeper}
        onClose={() => setOpenModalKeeper(false)}
      />
      <BulkDuplicateModal
        open={openModalBulkDuplicate}
        onClose={() => setOpenModalBulkDuplicate(false)}
        selectedSamples={selectedSamples}
        onSuccess={handleBulkDuplicateSuccess}
      />
      {loadingExport && <Loading />}
    </CreatePageList>
  );
};

export default SampleList;
